<script setup>
import { ref, computed, onMounted, onUnmounted, h } from 'vue';
import { Empty } from 'ant-design-vue';
import { ReloadOutlined, ColumnHeightOutlined } from '@ant-design/icons-vue';
import TableIndexColumn from './TableIndexColumn.vue';

const emptyImage = Empty.PRESENTED_IMAGE_SIMPLE;

const props = defineProps({
    title: {
        type: String,
        default: ''
    },
    columns: {
        type: Array,
        required: true
    },
    dataSource: {
        type: Array,
        default: () => []
    },
    pagination: {
        type: Object,
        default: () => ({
            total: 0,
            current: 1,
            pageSize: 10
        })
    },
    rowKey: {
        type: String,
        default: 'id'
    },
    bordered: {
        type: Boolean,
        default: false
    },
    showIndex: {
        type: Boolean,
        default: true
    },
    scroll: {
        type: Object,
        default: () => ({ x: 1500 })
    },
    showPagination: {
        type: Boolean,
        default: true
    },
    // 是否不使用外層 Card 包裹（避免額外邊框）
    noCard: {
        type: Boolean,
        default: false
    },
    // 表格展開配置
    expandable: {
        type: Object,
        default: undefined
    },
    // 行選擇配置
    rowSelection: {
        type: Object,
        default: undefined
    },
    childrenColumnName: {
        type: String,
        default: 'children'
    }
});

const emit = defineEmits(['refresh', 'change']);

// 表格密度設置
const tableSize = ref('middle');

const handleSizeChange = (size) => {
    tableSize.value = size;
};

const emitTableChange = (pagination) => {
    emit('change', {
        pagination: {
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total ?? props.pagination.total ?? 0
        }
    });
};

// 添加表格變化處理函數
const handleTableChange = (pagination, filters, sorter, extra) => {
    // 統一包成 { pagination, filters, sorter } 給父層
    emit('change', { pagination, filters, sorter, extra });
};

const tablePagination = computed(() => {
    if (!props.showPagination) {
        return false;
    }

    return {
        ...props.pagination,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        defaultPageSize: 20,
        size: 'small',
        position: ['bottomCenter'],
        hideOnSinglePage: false,
        showTotal: (total) => `總共 ${total} 筆`,
        locale: {
            items_per_page: '筆/頁',
            jump_to: '跳至',
            jump_to_confirm: '確定',
            page: '頁',
            prev_page: '上一頁',
            next_page: '下一頁',
            prev_5: '向前 5 頁',
            next_5: '向后 5 頁'
        },
        onChange: (page, pageSize) =>
            emitTableChange({
                current: page,
                pageSize,
                total: props.pagination.total
            }),
        onShowSizeChange: (page, pageSize) =>
            emitTableChange({
                current: page,
                pageSize,
                total: props.pagination.total
            })
    };
});

// 追蹤視窗寬度
const windowWidth = ref(window.innerWidth);

// 監聽視窗大小變化
const handleResize = () => {
    windowWidth.value = window.innerWidth;
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

// 計算橫向滾動：優先使用傳入的 scroll，否則行動裝置或欄數多時用內容寬度
const tableScroll = computed(() => {
    if (props.scroll && typeof props.scroll === 'object') return props.scroll;
    const isMobile = windowWidth.value <= 768;
    const hasScrollBehavior = props.columns.length > 6 || isMobile;
    return { x: hasScrollBehavior ? 'max-content' : '100%' };
});

// 表格本地化（排序提示文字、無資料文字）
const tableLocale = {
    emptyText: '暫無資料',
    cancelSort: '點擊以取消排序',
    triggerAsc: '點擊以升冪排序',
    triggerDesc: '點擊以降冪排序'
};

// 添加序號列到列定義中
const processedColumns = computed(() => {
    // 添加序號列作為第一列
    const indexColumn = {
        title: '#',
        key: 'tableIndex',
        width: 52,
        align: 'center',
        customRender: ({ index }) => {
            return h(TableIndexColumn, {
                index,
                pagination: props.pagination
            });
        }
    };

    const allColumns = props.showIndex
        ? [indexColumn, ...props.columns]
        : props.columns;

    return allColumns.map((col, index) => {
        if (index === allColumns.length - 1) {
            return {
                ...col
            };
        }
        return col;
    });
});
</script>

<template>
    <component :is="props.noCard ? 'div' : 'a-card'">
        <div class="flex justify-between items-center mb-2">
            <span v-if="title" class="text-lg font-medium">{{ title }}</span>
            <a-space :class="{ 'ml-auto': !title }">
                <slot name="titleActions"></slot>
                <a-tooltip title="刷新">
                    <a-button
                        class="table-toolbar-icon-btn"
                        @click="$emit('refresh')"
                    >
                        <template #icon><reload-outlined /></template>
                    </a-button>
                </a-tooltip>
                <a-tooltip title="密度">
                    <a-dropdown placement="bottom">
                        <a-button class="table-toolbar-icon-btn">
                            <template #icon>
                                <column-height-outlined />
                            </template>
                        </a-button>
                        <template #overlay>
                            <a-menu
                                @click="({ key }) => handleSizeChange(key)"
                                :selectedKeys="[tableSize]"
                            >
                                <a-menu-item key="default">寬鬆</a-menu-item>
                                <a-menu-item key="middle">中等</a-menu-item>
                                <a-menu-item key="small">緊湊</a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                </a-tooltip>
            </a-space>
        </div>

        <a-table
            :columns="processedColumns"
            :data-source="dataSource"
            :pagination="tablePagination"
            :size="tableSize"
            :row-key="rowKey"
            :scroll="tableScroll"
            :locale="tableLocale"
            :expandable="expandable"
            :children-column-name="childrenColumnName"
            :row-selection="rowSelection"
            @change="handleTableChange"
            :class="[
                'custom-table',
                { 'custom-table--no-index': !props.showIndex }
            ]"
        >
            <template #emptyText>
                <div class="table-empty-state">
                    <a-empty :image="emptyImage" description="暫無資料" />
                </div>
            </template>
            <template
                v-for="(_, name) in $slots"
                :key="name"
                #[name]="slotData"
            >
                <slot :name="name" v-bind="slotData"></slot>
            </template>
        </a-table>
    </component>
</template>

<style scoped>
.table-toolbar-icon-btn {
    width: 32px !important;
    min-width: 32px !important;
    height: 32px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.table-toolbar-icon-btn :deep(.anticon) {
    margin: 0 !important;
    line-height: 1 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.custom-table :deep(.ant-table-cell-fix-right) {
    background-color: #ffffff !important;
    box-shadow: -6px 0 6px -4px rgba(0, 0, 0, 0.12) !important;
}

/* 確保分頁選項在移動設備上顯示 */
.custom-table :deep(.ant-pagination-options) {
    display: inline-block !important;
}

.custom-table :deep(.ant-pagination-options-size-changer) {
    display: inline-block !important;
}

.custom-table :deep(.ant-select-selector) {
    min-width: 90px !important;
}

/* 移動設備的分頁樣式 */
@media screen and (max-width: 768px) {
    .custom-table :deep(.ant-pagination) {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        flex-wrap: wrap !important;
    }

    .custom-table :deep(.ant-pagination-options) {
        margin-left: 8px !important;
    }
}

/* 確保桌面版時表格寬度正常展開 */
@media screen and (min-width: 769px) {
    .custom-table :deep(.ant-table) {
        width: 100% !important;
    }
}

/* 表格 hover 效果 - 蒸氣波淺紫色 */
.custom-table :deep(.ant-table-tbody > tr:hover > td),
.custom-table :deep(.ant-table-tbody > tr:hover),
.custom-table :deep(.ant-table-tbody > tr:hover > th),
.custom-table :deep(.ant-table-tbody tr:hover),
.custom-table :deep(.ant-table-tbody tr:hover td),
.custom-table :deep(.ant-table-tbody tr:hover th),
.custom-table :deep(.ant-table-row:hover),
.custom-table :deep(.ant-table-row:hover td),
.custom-table :deep(.ant-table-row:hover th) {
    background: rgba(42, 74, 122, 0.1) !important;
    background-color: rgba(42, 74, 122, 0.1) !important;
    color: #1a1a2a !important;
}

/* 強制覆蓋所有可能的 hover 狀態 */
.custom-table :deep(.ant-table-tbody tr) {
    background: rgba(255, 255, 255, 0.6) !important;
    background-color: rgba(255, 255, 255, 0.6) !important;
}

.custom-table :deep(.ant-table-tbody tr td) {
    background: rgba(255, 255, 255, 0.6) !important;
    background-color: rgba(255, 255, 255, 0.6) !important;
    color: #1a1a2a !important;
}

/* 禁用表格行的過渡效果，但保留按鈕的 hover 效果 */
.custom-table :deep(.ant-table-tbody tr),
.custom-table :deep(.ant-table-tbody tr td),
.custom-table :deep(.ant-table-tbody tr th),
.custom-table :deep(.ant-table-tbody tr div),
.custom-table :deep(.ant-table-tbody tr span) {
    transition: none !important;
}

/* 表格行 hover 時淡紫色背景 */
.custom-table :deep(.ant-table-tbody tr:hover),
.custom-table :deep(.ant-table-tbody tr:hover td),
.custom-table :deep(.ant-table-tbody tr:hover th) {
    background: rgba(42, 74, 122, 0.1) !important;
    background-color: rgba(42, 74, 122, 0.1) !important;
    color: #1a1a2a !important;
    transition: none !important;
}

/* 表格行 hover 時，保持 div 和 span 的樣式，但不影響標籤、按鈕與按鈕內文字 */
/* 注意：需排除 .ant-badge-status-dot，否則 badge 的綠色點點會因 background: transparent 被蓋掉 */
.custom-table
    :deep(
        .ant-table-tbody
            tr:hover
            div:not(.ant-tag):not(.ant-btn):not([class*='action-btn-'])
    ),
.custom-table
    :deep(
        .ant-table-tbody
            tr:hover
            span:not(.ant-tag):not(.ant-btn):not(.ant-badge-status-dot):not(
                :is(.ant-btn *)
            )
    ) {
    background: transparent !important;
    background-color: transparent !important;
    color: #1a1a2a !important;
    transition: none !important;
}

/* 主要 CTA（完課計分等）：紫底白字，列／按鈕 hover 與否皆固定 */
.custom-table :deep(.ant-table-tbody .action-btn-primary),
.custom-table :deep(.ant-table-tbody tr:hover .action-btn-primary),
.custom-table :deep(.ant-table-tbody .action-buttons .action-btn-primary) {
    border-color: transparent !important;
    background: #2a4a7a !important;
    background-color: #2a4a7a !important;
    color: #ffffff !important;
    -webkit-text-fill-color: #ffffff !important;
    box-shadow: 0 2px 8px rgba(109, 40, 217, 0.28) !important;
}

.custom-table :deep(.ant-table-tbody .action-btn-primary:hover),
.custom-table :deep(.ant-table-tbody tr:hover .action-btn-primary:hover),
.custom-table
    :deep(.ant-table-tbody .action-buttons .action-btn-primary:hover) {
    background: linear-gradient(135deg, #1e3a5f, #1e3a5f) !important;
    background-color: #1e3a5f !important;
    color: #ffffff !important;
    -webkit-text-fill-color: #ffffff !important;
}

.custom-table :deep(.ant-table-tbody .action-btn-primary > span),
.custom-table :deep(.ant-table-tbody tr:hover .action-btn-primary > span),
.custom-table
    :deep(.ant-table-tbody .action-buttons .action-btn-primary > span) {
    color: #ffffff !important;
    -webkit-text-fill-color: #ffffff !important;
    background: transparent !important;
    background-color: transparent !important;
}

.custom-table :deep(.ant-table-tbody tr:hover .action-btn-edit),
.custom-table :deep(.ant-table-tbody tr:hover .action-btn-edit > span) {
    color: #1d4ed8 !important;
}

.custom-table :deep(.ant-table-tbody tr:hover .action-btn-delete),
.custom-table :deep(.ant-table-tbody tr:hover .action-btn-delete > span),
.custom-table
    :deep(.ant-table-tbody tr:hover .action-btn-delete.ant-btn-dangerous),
.custom-table
    :deep(
        .ant-table-tbody tr:hover .action-btn-delete.ant-btn-dangerous > span
    ) {
    color: #dc2626 !important;
}

/* 確保按鈕有 pointer cursor */
.custom-table :deep(.ant-table-tbody .ant-btn) {
    cursor: pointer !important;
}

/* 搜尋框蒸氣波淺色版樣式 */
.custom-table :deep(.ant-input-search) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(42, 74, 122, 0.4) !important;
    border-radius: 8px !important;
}

.custom-table :deep(.ant-input-search .ant-input) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: none !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
}

.custom-table :deep(.ant-input-search .ant-input:focus) {
    border: none !important;
    box-shadow: none !important;
}

.custom-table :deep(.ant-input-search .ant-input::placeholder) {
    color: rgba(26, 26, 42, 0.6) !important;
}

.custom-table :deep(.ant-input-search .ant-input-search-button) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: none !important;
    border-left: 1px solid rgba(42, 74, 122, 0.4) !important;
}

.custom-table :deep(.ant-input-search .ant-input-search-button:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-left: 1px solid #2a4a7a !important;
}

.custom-table :deep(.ant-input-search .ant-input-search-button .anticon) {
    color: #1a1a2a !important;
}

.custom-table :deep(.ant-input-search .ant-input-search-button:hover .anticon) {
    color: #2a4a7a !important;
}

/* 密度按鈕和下拉選單蒸氣波淺色版樣式 */
.custom-table :deep(.ant-dropdown) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 2px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

/* 密度按鈕 dropdown 位置對齊 */
.custom-table :deep(.ant-dropdown-placement-bottomLeft) {
    transform: translateX(-50%) !important;
}

.custom-table :deep(.ant-dropdown-placement-bottom) {
    transform: translateX(-50%) !important;
}

/* 確保 dropdown 與按鈕中心對齊 */
.custom-table :deep(.ant-dropdown) {
    left: 50% !important;
    transform: translateX(-50%) !important;
}

/* 密度按鈕的 dropdown 特殊定位 */
.custom-table :deep(.ant-dropdown-menu) {
    min-width: 120px !important;
    text-align: center !important;
}

/* 更強力的下拉選單樣式覆蓋 */
.custom-table :deep(.ant-dropdown-menu),
.custom-table :deep(.ant-dropdown-menu.ant-dropdown-menu),
.custom-table :deep(.ant-dropdown-menu.ant-dropdown-menu-root),
.custom-table :deep(.ant-dropdown-menu.ant-dropdown-menu-vertical) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 2px solid transparent !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
    padding: 8px !important;
}

.custom-table :deep(.ant-dropdown-menu-item),
.custom-table :deep(.ant-dropdown-menu-item.ant-dropdown-menu-item),
.custom-table :deep(.ant-dropdown-menu-item.ant-dropdown-menu-item-only-child) {
    background: transparent !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
    margin: 2px 0 !important;
    padding: 8px 12px !important;
}

.custom-table :deep(.ant-dropdown-menu-item:hover),
.custom-table :deep(.ant-dropdown-menu-item.ant-dropdown-menu-item:hover) {
    background: rgba(42, 74, 122, 0.2) !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-dropdown-menu-item-selected),
.custom-table :deep(.ant-dropdown-menu-item.ant-dropdown-menu-item-selected) {
    background: rgba(42, 74, 122, 0.3) !important;
    color: #2a4a7a !important;
}

/* 針對可能的其他選擇器 */
.custom-table :deep(.ant-dropdown-menu-item-active) {
    background: rgba(42, 74, 122, 0.2) !important;
    color: #2a4a7a !important;
}

/* 全域強制覆蓋 - 最高優先級 */
:deep(.ant-dropdown-menu) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 2px solid transparent !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
    padding: 8px !important;
}

:deep(.ant-dropdown-menu-item) {
    background: transparent !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
    margin: 2px 0 !important;
    padding: 8px 12px !important;
}

:deep(.ant-dropdown-menu-item:hover) {
    background: rgba(42, 74, 122, 0.2) !important;
    color: #2a4a7a !important;
}

:deep(.ant-dropdown-menu-item-selected) {
    background: rgba(42, 74, 122, 0.3) !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-dropdown::before) {
    content: '' !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    border-radius: 12px !important;
    padding: 2px !important;
    background: linear-gradient(
        45deg,
        #64748b,
        #2a4a7a,
        #3b6ea5,
        #0e7490
    ) !important;
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0) !important;
    mask-composite: exclude !important; /* Firefox 需用標準值 */
    -webkit-mask-composite: xor !important;
}

.custom-table :deep(.ant-menu) {
    background: transparent !important;
    border: none !important;
}

.custom-table :deep(.ant-menu-item) {
    background: transparent !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
    margin: 4px !important;
}

.custom-table :deep(.ant-menu-item:hover) {
    background: rgba(42, 74, 122, 0.2) !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-menu-item-selected) {
    background: rgba(42, 74, 122, 0.3) !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-menu-item-selected::after) {
    display: none !important;
}

/* 統一按鈕樣式（排除語意 action 按鈕） */
.custom-table
    :deep(.ant-btn:not([class*='action-btn-']):not(.table-toolbar-icon-btn)) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 50px !important;
    font-weight: 500 !important;
    transition: all 0.3s ease !important;
    padding: 8px 16px !important;
    min-width: auto !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
    margin: 0 2px !important;
    height: auto !important;
    line-height: 1.2 !important;
}

/* 按鈕容器樣式 */
.custom-table :deep(.ant-space) {
    display: flex !important;
    flex-wrap: nowrap !important;
    gap: 4px !important;
}

.custom-table :deep(.ant-space-item) {
    flex-shrink: 0 !important;
}

/* 表格行 hover 時，按鈕保持原本樣式 */
.custom-table
    :deep(.ant-table-tbody tr:hover .ant-btn:not([class*='action-btn-'])) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    box-shadow: none !important;
    transition: none !important;
}

/* 只有直接 hover 按鈕時才有效果 */
.custom-table
    :deep(.ant-table-tbody .ant-btn:not([class*='action-btn-']):hover) {
    background: rgba(255, 255, 255, 1) !important;
    border-color: rgba(200, 200, 210, 0.6) !important;
    color: #2a2a3a !important;
    box-shadow: 0 2px 8px rgba(200, 200, 210, 0.2) !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-btn:not([class*='action-btn-']):focus) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    box-shadow: none !important;
}

.custom-table
    :deep(
        .ant-btn-primary:not(.action-btn-primary):not([class*='action-btn-'])
    ) {
    background: transparent !important;
    border: 2px solid #2a4a7a !important;
    color: #2a4a7a !important;
}

/* 表格行 hover 時，主要按鈕保持原本樣式 */
.custom-table
    :deep(
        .ant-table-tbody
            tr:hover
            .ant-btn-primary:not(.action-btn-primary):not(
                [class*='action-btn-']
            )
    ) {
    background: transparent !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
    box-shadow: none !important;
    transition: none !important;
}

/* 只有直接 hover 主要按鈕時才有效果 */
.custom-table
    :deep(
        .ant-table-tbody
            .ant-btn-primary:not(.action-btn-primary):not(
                [class*='action-btn-']
            ):hover
    ) {
    background: transparent !important;
    border-color: #0e7490 !important;
    color: #0e7490 !important;
    box-shadow: 0 0 10px rgba(168, 255, 246, 0.3) !important;
    transition: all 0.3s ease !important;
}

.custom-table
    :deep(
        .ant-btn-primary:not(.action-btn-primary):not(
                [class*='action-btn-']
            ):focus
    ) {
    background: transparent !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
    box-shadow: none !important;
}

.custom-table
    :deep(.ant-btn-danger:not(.action-btn-delete):not([class*='action-btn-'])) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #ff4d4f !important;
}

/* 表格行 hover 時，危險按鈕保持原本樣式 */
.custom-table
    :deep(
        .ant-table-tbody
            tr:hover
            .ant-btn-danger:not(.action-btn-delete):not([class*='action-btn-'])
    ) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #ff4d4f !important;
    box-shadow: none !important;
    transition: none !important;
}

/* 只有直接 hover 危險按鈕時才有效果 */
.custom-table
    :deep(
        .ant-table-tbody
            .ant-btn-danger:not(.action-btn-delete):not(
                [class*='action-btn-']
            ):hover
    ) {
    background: rgba(255, 255, 255, 1) !important;
    border-color: rgba(200, 200, 210, 0.6) !important;
    color: #ff4d4f !important;
    box-shadow: 0 2px 8px rgba(200, 200, 210, 0.2) !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-btn-success) {
    background: rgba(82, 196, 26, 0.2) !important;
    border-color: #52c41a !important;
    color: #52c41a !important;
}

/* 表格行 hover 時，成功按鈕保持原本樣式 */
.custom-table :deep(.ant-table-tbody tr:hover .ant-btn-success) {
    background: rgba(82, 196, 26, 0.2) !important;
    border-color: #52c41a !important;
    color: #52c41a !important;
    box-shadow: none !important;
    transition: none !important;
}

/* 只有直接 hover 成功按鈕時才有效果 */
.custom-table :deep(.ant-table-tbody .ant-btn-success:hover) {
    background: rgba(82, 196, 26, 0.3) !important;
    border-color: #52c41a !important;
    color: #52c41a !important;
    box-shadow: 0 0 10px rgba(82, 196, 26, 0.3) !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-btn .anticon) {
    color: #1a1a2a !important;
    transition: none !important;
}

.custom-table :deep(.ant-btn:hover .anticon) {
    color: #1a1a2a !important;
}

.custom-table :deep(.ant-btn-primary .anticon) {
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-btn-primary:hover .anticon) {
    color: #0e7490 !important;
}

.custom-table :deep(.ant-btn-danger .anticon) {
    color: #ff4d4f !important;
}

.custom-table :deep(.ant-btn-danger:hover .anticon) {
    color: #ff4d4f !important;
}

.custom-table :deep(.ant-btn-success .anticon) {
    color: #52c41a !important;
}

.custom-table :deep(.ant-btn-success:hover .anticon) {
    color: #52c41a !important;
}

/* 表格操作欄按鈕：比一般工具列更緊湊（不含語意 action class） */
.custom-table :deep(.ant-table-tbody .ant-btn:not([class*='action-btn-'])),
.custom-table
    :deep(.ant-table-tbody tr:hover .ant-btn:not([class*='action-btn-'])),
.custom-table
    :deep(.ant-table-tbody .ant-btn:not([class*='action-btn-']):focus) {
    height: 24px !important;
    min-width: auto !important;
    padding: 0 8px !important;
    margin: 0 !important;
    border-radius: 7px !important;
    border: 1px solid rgba(42, 74, 122, 0.55) !important;
    background: rgba(255, 255, 255, 0.92) !important;
    color: #6f3bbd !important;
    font-size: 12px !important;
    line-height: 22px !important;
    font-weight: 700 !important;
    box-shadow: none !important;
    transition: all 0.18s ease !important;
}

.custom-table :deep(.ant-table-tbody .ant-btn-primary:not(.action-btn-primary)),
.custom-table
    :deep(.ant-table-tbody .ant-btn-color-primary:not(.action-btn-primary)),
.custom-table
    :deep(.ant-table-tbody tr:hover .ant-btn-primary:not(.action-btn-primary)),
.custom-table
    :deep(
        .ant-table-tbody
            tr:hover
            .ant-btn-color-primary:not(.action-btn-primary)
    ) {
    border-color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.1) !important;
    color: #6f3bbd !important;
}

.custom-table :deep(.ant-table-tbody .ant-btn-danger),
.custom-table
    :deep(.ant-table-tbody .ant-btn-dangerous:not(.action-btn-delete)),
.custom-table :deep(.ant-table-tbody tr:hover .ant-btn-danger),
.custom-table
    :deep(
        .ant-table-tbody tr:hover .ant-btn-dangerous:not(.action-btn-delete)
    ) {
    border-color: rgba(239, 68, 68, 0.55) !important;
    background: rgba(255, 255, 255, 0.9) !important;
    color: #dc2626 !important;
}

/* 只有直接 hover 危險按鈕時才有效果 */
.custom-table :deep(.ant-table-tbody .ant-btn-danger:hover),
.custom-table
    :deep(.ant-table-tbody .ant-btn-dangerous:not(.action-btn-delete):hover) {
    background: rgba(239, 68, 68, 0.08) !important;
    border-color: #ef4444 !important;
    color: #b91c1c !important;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.18) !important;
    transition: all 0.3s ease !important;
}
.custom-table
    :deep(
        .ant-table-tbody
            .ant-btn:hover:not([class*='action-btn-']):not(.ant-btn-danger):not(
                .ant-btn-dangerous
            )
    ),
.custom-table
    :deep(.ant-table-tbody .ant-btn-primary:hover:not(.action-btn-primary)),
.custom-table
    :deep(
        .ant-table-tbody .ant-btn-color-primary:hover:not(.action-btn-primary)
    ) {
    border-color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.16) !important;
    color: #1a4268 !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.18) !important;
    transform: translateY(-1px);
}

.custom-table :deep(.ant-table-tbody .ant-space) {
    flex-wrap: wrap !important;
    gap: 4px !important;
}

/* 表格基本樣式  */
.custom-table :deep(.ant-table) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 12px !important;
    overflow: hidden !important;
}

/* 平均欄寬：固定表格佈局，所有欄位等分（內容使用省略） */
.custom-table :deep(.ant-table table) {
    table-layout: auto !important;
    width: 100% !important;
}

.custom-table :deep(.ant-table-container) {
    border-radius: 12px !important;
    overflow: hidden !important;
}

.custom-table :deep(.ant-table-thead > tr > th) {
    background: rgba(255, 255, 255, 0.9) !important;
    color: #64748b !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-bottom: 1px solid rgba(42, 74, 122, 0.3) !important;
}

.custom-table :deep(.ant-table-measure-row),
.custom-table :deep(tr.ant-table-measure-row) {
    height: 0 !important;
    visibility: collapse !important;
    pointer-events: none !important;
}

.custom-table :deep(.ant-table-measure-row td) {
    height: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    line-height: 0 !important;
    font-size: 0 !important;
}

.custom-table :deep(.ant-table-placeholder > td) {
    background: rgba(255, 255, 255, 0.4) !important;
    border-bottom: none !important;
    text-align: center !important;
}

.table-empty-state {
    padding: 40px 16px 48px;
    text-align: center;
}

.table-empty-state :deep(.ant-empty) {
    margin-inline: auto;
}

.table-empty-state :deep(.ant-empty-image) {
    height: 56px;
    margin-bottom: 8px;
    opacity: 0.85;
}

.table-empty-state :deep(.ant-empty-description) {
    color: #8a8a9a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    letter-spacing: 0.02em;
}

.custom-table :deep(.ant-table-tbody > tr > td) {
    background: rgba(255, 255, 255, 0.6) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

/* 標籤樣式 - 完全橢圓 */
.custom-table :deep(.ant-tag) {
    border-radius: 50px !important;
    padding: 4px 12px !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border: 1px solid transparent !important;
    background-clip: padding-box !important;
    cursor: default !important;
}

/* 完全移除標籤的 hover 效果 - 直接覆寫 Ant Design 樣式 */
.custom-table :deep(.ant-tag:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag),
.custom-table :deep(.ant-table-tbody .ant-tag:hover) {
    transform: none !important;
    box-shadow: none !important;
    cursor: default !important;
    /* 強制保持原本的樣式，不讓任何 hover 效果生效 */
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

/* 針對不同顏色的標籤，強制保持原本樣式 */
.custom-table :deep(.ant-tag-purple:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag-purple) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-tag-green:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag-green) {
    background: rgba(82, 196, 26, 0.2) !important;
    border-color: #52c41a !important;
    color: #52c41a !important;
}

.custom-table :deep(.ant-tag-red:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag-red) {
    background: rgba(255, 77, 79, 0.2) !important;
    border-color: #ff4d4f !important;
    color: #ff4d4f !important;
}

.custom-table :deep(.ant-tag-blue:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag-blue) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-tag-cyan:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag-cyan) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-tag-orange:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag-orange) {
    background: rgba(255, 168, 122, 0.2) !important;
    border-color: #ffa87a !important;
    color: #ffa87a !important;
}

.custom-table :deep(.ant-tag-default:hover),
.custom-table :deep(.ant-table-tbody tr:hover .ant-tag-default) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.6) !important;
    color: #2a2a3a !important;
}

.custom-table :deep(.ant-tag:active) {
    transform: none !important;
    box-shadow: none !important;
}

.custom-table :deep(.ant-tag-purple) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-tag-green) {
    background: rgba(82, 196, 26, 0.2) !important;
    border-color: #52c41a !important;
    color: #52c41a !important;
}

.custom-table :deep(.ant-tag-red) {
    background: rgba(255, 77, 79, 0.2) !important;
    border-color: #ff4d4f !important;
    color: #ff4d4f !important;
}

.custom-table :deep(.ant-tag-blue) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-tag-cyan) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-tag-orange) {
    background: rgba(255, 168, 122, 0.2) !important;
    border-color: #ffa87a !important;
    color: #ffa87a !important;
}

.custom-table :deep(.ant-tag-default) {
    background: rgba(255, 255, 255, 0.8) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

/* 分頁器蒸氣波淺色版樣式 - 重新設計 */
.custom-table :deep(.ant-pagination) {
    background: transparent !important;
    color: #2a2a3a !important;
}

/* 分頁按鈕 - 完全橢圓 */
.custom-table :deep(.ant-pagination-item) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 50px !important;
    margin: 0 4px !important;
    min-width: 32px !important;
    height: 32px !important;
    line-height: 30px !important;
    text-align: center !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-pagination-item a) {
    color: #2a2a3a !important;
}

.custom-table :deep(.ant-pagination-item:hover a) {
    color: #2a4a7a !important;
}

.custom-table :deep(.ant-pagination-item:hover) {
    background: rgba(255, 255, 255, 0.95) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.25) !important;
}

.custom-table :deep(.ant-pagination-item-active) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border-color: #2a4a7a !important;
    color: #0d0d0d !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.35) !important;
}

.custom-table :deep(.ant-pagination-item-active a) {
    color: #0d0d0d !important;
}

.custom-table :deep(.ant-pagination-item-active:hover) {
    background: rgba(42, 74, 122, 0.4) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
    box-shadow: none !important;
}

/* 上一頁/下一頁按鈕 - 完全橢圓 */
.custom-table :deep(.ant-pagination-prev),
.custom-table :deep(.ant-pagination-next) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 50px !important;
    margin: 0 4px !important;
    min-width: 32px !important;
    height: 32px !important;
    line-height: 30px !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-pagination-prev:hover),
.custom-table :deep(.ant-pagination-next:hover) {
    background: rgba(255, 255, 255, 0.95) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.25) !important;
}

.custom-table :deep(.ant-pagination-prev .anticon),
.custom-table :deep(.ant-pagination-next .anticon) {
    color: #2a2a3a !important;
    transition: color 0.3s ease !important;
}

.custom-table :deep(.ant-pagination-prev:hover .anticon),
.custom-table :deep(.ant-pagination-next:hover .anticon) {
    color: #2a4a7a !important;
}

/* 跳轉按鈕 - 完全橢圓 */
.custom-table :deep(.ant-pagination-jump-prev),
.custom-table :deep(.ant-pagination-jump-next) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: white !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 50px !important;
    margin: 0 4px !important;
    min-width: 32px !important;
    height: 32px !important;
    line-height: 30px !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-pagination-jump-prev:hover),
.custom-table :deep(.ant-pagination-jump-next:hover) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: white !important;
    box-shadow: none !important;
}

.custom-table :deep(.ant-pagination-jump-prev .anticon),
.custom-table :deep(.ant-pagination-jump-next .anticon) {
    color: white !important;
    transition: color 0.3s ease !important;
}

.custom-table :deep(.ant-pagination-jump-prev:hover .anticon),
.custom-table :deep(.ant-pagination-jump-next:hover .anticon) {
    color: #0e7490 !important;
}

/* 總數文字 */
.custom-table :deep(.ant-pagination-total-text) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 14px !important;
    margin-right: 16px !important;
    height: 32px !important;
    line-height: 32px !important;
    display: flex !important;
    align-items: center !important;
}

/* 頁面大小選擇器 */
.custom-table :deep(.ant-pagination-options) {
    color: white !important;
    margin-left: 16px !important;
}

.custom-table :deep(.ant-pagination-options-size-changer) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: white !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 50px !important;
    height: 32px !important;
    line-height: 30px !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-pagination-options-size-changer:hover) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: white !important;
    box-shadow: none !important;
}

.custom-table :deep(.ant-pagination-options-size-changer .ant-select-selector) {
    background: transparent !important;
    border: none !important;
    color: white !important;
    font-family: 'Courier New', monospace !important;
    height: 30px !important;
    line-height: 30px !important;
}

.custom-table
    :deep(.ant-pagination-options-size-changer .ant-select-selection-item) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    line-height: 30px !important;
}

.custom-table :deep(.ant-pagination-options-size-changer .ant-select-arrow) {
    color: #1a1a2a !important;
}

.custom-table
    :deep(.ant-pagination-options-size-changer:hover .ant-select-arrow) {
    color: #0e7490 !important;
}

/* 頁面大小選擇器下拉選單 */
.custom-table :deep(.ant-select-dropdown) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 16px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
    min-width: 120px !important;
    width: auto !important;
}

.custom-table :deep(.ant-select-item) {
    background: transparent !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 12px !important;
    margin: 2px 4px !important;
    padding: 8px 12px !important;
    transition: all 0.2s ease !important;
}

.custom-table :deep(.ant-select-item-option) {
    background: transparent !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 12px !important;
    margin: 2px 4px !important;
    padding: 8px 12px !important;
    transition: all 0.2s ease !important;
}

.custom-table :deep(.ant-select-item:hover) {
    background: rgba(168, 255, 246, 0.2) !important;
    color: #1a1a2a !important;
}

.custom-table :deep(.ant-select-item-option-selected) {
    background: rgba(42, 74, 122, 0.4) !important;
    color: #1a1a2a !important;
}

.custom-table :deep(.ant-select-item-option-active) {
    background: rgba(168, 255, 246, 0.2) !important;
    color: #1a1a2a !important;
}

/* 快速跳轉 */
.custom-table :deep(.ant-pagination-options-quick-jumper) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    margin-left: 16px !important;
}

.custom-table :deep(.ant-pagination-options-quick-jumper input) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 50px !important;
    height: 32px !important;
    line-height: 30px !important;
    transition: all 0.3s ease !important;
}

.custom-table :deep(.ant-pagination-options-quick-jumper input:hover) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #1a1a2a !important;
    box-shadow: none !important;
}

.custom-table :deep(.ant-pagination-options-quick-jumper input:focus) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #1a1a2a !important;
    box-shadow: none !important;
}
</style>
